import type { Resource } from '__types__';
import type {
  FieldSpec,
  FieldSpecOf
} from 'v5/core/entity-schema/entitySchema.types';
import type {
  ResourceKinds,
  UnitValues
} from 'v5/core/entity-values/values.types';
import {
  applyFieldUpdates,
  type FieldUpdates
} from 'v5/core/form-schema/formKinds.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';

/** What picking an agent copies onto the represented resource (v1 `getAgentData`). */
export type AgentDetails = {
  email: string | null;
  phone: string | null;
  organisationId: number | null;
};

/** Contact-details type the copied email / phone get (legacy `CONTACT_INFO_TYPES`). */
const AGENT_CONTACT_TYPE = 'agent';

function firstValue(rows: ReadonlyArray<{ value_1?: string }> | undefined) {
  const value = rows?.[0]?.value_1 ?? '';
  return value === '' ? null : value;
}

/** The agent's first email and phone (as the backend lists them) and its organisation. */
export function agentDetailsOf(agent: Resource): AgentDetails {
  return {
    email: firstValue(agent.emails),
    phone: firstValue(agent.phone_numbers),
    organisationId: agent.organisation_id ?? null
  };
}

/** The `represented_by_id` reference: picking a resource there copies its details. */
export function isAgentRef(spec: FieldSpecOf<'entity-ref'>): boolean {
  return spec.column === 'represented_by_id';
}

/**
 * What an agent pick does to the represented resource's fields, per kind.
 * As in v1 (`RESOURCE_CONNECT`): the agent's email and phone are appended
 * with the type "agent", never deduplicated; its organisation replaces the
 * resource's, unless the agent has none. Other fields stay as they are.
 */
export function agentPickUpdates(
  agent: AgentDetails
): FieldUpdates<ResourceKinds> {
  return {
    'entity-ref': spec =>
      spec.column === 'organisation_id' && agent.organisationId !== null
        ? agent.organisationId
        : undefined,
    'contact-details': (spec, items) => {
      const copied =
        spec.detailType === 'EMAIL'
          ? agent.email
          : spec.detailType === 'PHONE'
            ? agent.phone
            : null;
      if (copied === null) return undefined;
      return [
        ...items,
        { rowId: rowIdUtils.local(), value: copied, type: AGENT_CONTACT_TYPE }
      ];
    }
  };
}

/** A unit's values after an agent pick; `null` when the pick touches none of its fields. */
export function unitValuesAfterAgentPick(
  fields: ReadonlyArray<FieldSpec>,
  values: UnitValues,
  agent: AgentDetails
): UnitValues | null {
  return applyFieldUpdates(fields, values, agentPickUpdates(agent));
}
